import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { BasketSummary, CheckoutPreview, Profile } from '@lr/shared';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  getGuestBasket,
  removeFromGuestBasket
} from '../lib/guestBasket';
interface ShippingForm {
  shipping_line1: string;
  shipping_line2: string;
  shipping_city: string;
  shipping_postcode: string;
  shipping_country: string;
}
interface GuestBasketLine {
  id: string;
  slug: string;
  name: string;
  credit_price: number;
  main_image_url: string | null;
  brand_name: string | null;
}
export function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, refreshProfile } = useAuth();
  const [basket, setBasket] = useState<BasketSummary | null>(null);
  const [guestLines, setGuestLines] = useState<GuestBasketLine[] | null>(null);
  const [preview, setPreview] = useState<CheckoutPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [shipping, setShipping] = useState<ShippingForm>({
    shipping_line1: '',
    shipping_line2: '',
    shipping_city: '',
    shipping_postcode: '',
    shipping_country: 'GB'
  });
  async function refresh() {
    setLoading(true);
    try {
      if (!user) {
        // Guest mode: build the basket display from localStorage by fetching
        // the items in ONE bulk request. The original per-item GETs stalled
        // inside the Instagram in-app browser (Promise.all never resolved
        // because some fetches silently never returned), making the total
        // read as 0 even with items in the bag.
        const ids = getGuestBasket();
        if (ids.length === 0) {
          setGuestLines([]);
          setBasket(null);
          setPreview(null);
          return;
        }
        const fetched = (await apiFetch('/api/catalog/items/by-ids', {
          method: 'POST',
          body: JSON.stringify({ ids })
        })) as Array<{
          id: string;
          slug: string;
          name: string;
          credit_price: number;
          main_image_url: string | null;
          brand?: { name: string } | null;
        }>;
        const map = new Map(fetched.map((f) => [f.id, f]));
        for (const id of ids) {
          if (!map.has(id)) removeFromGuestBasket(id);
        }
        const lines: GuestBasketLine[] = ids
          .map((id) => map.get(id))
          .filter((f): f is NonNullable<typeof f> => !!f)
          .map((f) => ({
            id: f.id,
            slug: f.slug,
            name: f.name,
            credit_price: f.credit_price,
            main_image_url: f.main_image_url,
            brand_name: f.brand?.name ?? null
          }));
        setGuestLines(lines);
        setBasket(null);
        setPreview(null);
        return;
      }
      const [b, p] = await Promise.all([
        apiFetch<BasketSummary>('/api/basket'),
        apiFetch<CheckoutPreview>('/api/checkout/preview')
      ]);
      setBasket(b);
      setPreview(p);
      setGuestLines(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load checkout');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, [user]);
  // When Stripe redirects back after a successful delivery payment, the URL
  // has ?session_id=… . Call the confirm endpoint which runs place_order and
  // returns the finished order.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    void apiFetch<{
      order: { order_number: string };
    }>('/api/checkout/rental-delivery/confirm', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId })
    })
      .then((r) => {
        setConfirmed(r.order.order_number ?? 'Confirmed');
        // Clean the session_id out of the URL so a refresh doesn't retry.
        navigate('/checkout', { replace: true });
      })
      .catch((e) => {
        setError(
          e instanceof Error
            ? e.message
            : 'Payment succeeded but we couldn\'t finalise your order — contact support.'
        );
      });
  }, [location.search, navigate]);
  useEffect(() => {
    if (!profile) return;
    setShipping((s) => ({
      shipping_line1: s.shipping_line1 || profile.shipping_line1 || '',
      shipping_line2: s.shipping_line2 || profile.shipping_line2 || '',
      shipping_city: s.shipping_city || profile.shipping_city || '',
      shipping_postcode: s.shipping_postcode || profile.shipping_postcode || '',
      shipping_country: s.shipping_country || profile.shipping_country || 'GB'
    }));
  }, [profile]);
  const shippingValid =
    shipping.shipping_line1.trim().length > 0 &&
    shipping.shipping_city.trim().length > 0 &&
    shipping.shipping_postcode.trim().length > 0;
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const expiresAt = basket?.expires_at ? new Date(basket.expires_at).getTime() : null;
  const msLeft = expiresAt ? Math.max(0, expiresAt - now) : null;
  const isExpired = msLeft !== null && msLeft <= 0 && (basket?.items.length ?? 0) > 0;
  useEffect(() => {
    if (!isExpired) return;
    void apiFetch('/api/basket/release-expired', { method: 'POST' })
      .catch(() => {})
      .finally(() => void refresh());
  }, [isExpired]);
  async function removeItem(itemId: string) {
    await apiFetch('/api/basket/remove', {
      method: 'POST',
      body: JSON.stringify({ item_id: itemId })
    });
    window.dispatchEvent(new CustomEvent('lr:basket-changed'));
    await refresh();
  }
  async function placeOrder() {
    if (!consent) return;
    if (!shippingValid) {
      setError('Please fill in your delivery address.');
      return;
    }
    setPlacing(true);
    setError(null);
    try {
      await apiFetch<Profile>('/api/account/me', {
        method: 'PATCH',
        body: JSON.stringify(shipping)
      }).catch(() => {});
      if (refreshProfile) await refreshProfile();
      const res = await apiFetch<{
        url: string | null;
        order?: { order_number: string };
      }>('/api/checkout/place', {
        method: 'POST',
        body: JSON.stringify({
          shipping: {
            line1: shipping.shipping_line1,
            line2: shipping.shipping_line2 || null,
            city: shipping.shipping_city,
            postcode: shipping.shipping_postcode,
            country: shipping.shipping_country
          }
        })
      });
      if (res.url) {
        // Redirect to Stripe hosted page to pay the £4.99 delivery.
        window.location.href = res.url;
        return;
      }
      if (res.order) {
        // No payment step (Stripe off, or £0 order) — go straight to confirmed.
        setConfirmed(res.order.order_number ?? 'Confirmed');
      } else {
        throw new Error('Unexpected checkout response');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not place order');
      setPlacing(false);
    }
  }
  if (loading) {
    return (
      <section className="screen">
        <div className="scroll" style={{ padding: '40px 22px', color: 'var(--ink-soft)' }}>
          Loading…
        </div>
      </section>
    );
  }
  if (confirmed) {
    return (
      <section className="screen">
        <div className="scroll narrow-scroll">
          <div className="success">
            <div className="check">
              <Check />
            </div>
            <h2>YOU&apos;RE IN</h2>
            <div className="p">
              Your rotation is confirmed.
              <br />
              Order #{confirmed}. We&apos;re getting your items ready.
            </div>
            <div className="next-list">
              <div className="h">WHAT HAPPENS NEXT?</div>
              <div className="row">
                <div className="ic"><PackageIcon /></div>
                <div className="t">We&apos;ll quality check and pack your items.</div>
              </div>
              <div className="row">
                <div className="ic"><TruckIcon /></div>
                <div className="t">Dispatched within 2–3 working days.</div>
              </div>
              <div className="row">
                <div className="ic"><MailIcon /></div>
                <div className="t">You&apos;ll receive a tracking email as soon as it&apos;s on the way.</div>
              </div>
            </div>
          </div>
          <div className="footer-cta pad-bottom">
            <Link to="/account" className="cta-full">
              <span>VIEW MY ROTATION</span>
              <Arrow />
            </Link>
          </div>
        </div>
      </section>
    );
  }
  // Guest mode — sign-in gate.
  if (!user) {
    const lines = guestLines ?? [];
    const guestTotal = lines.reduce((acc, l) => acc + l.credit_price, 0);
    if (lines.length === 0) {
      return (
        <section className="screen">
          <div className="scroll narrow-scroll">
            <div className="ck-head">
              <h1>CHECKOUT</h1>
              <div className="p">Your bag is empty.</div>
            </div>
            <div className="empty-grid">
              <div className="h">Nothing in your bag yet</div>
              <div className="p">Browse the rotation and add your first piece.</div>
              <Link to="/browse" className="chip" style={{ background: 'var(--ink)', color: '#fff' }}>
                BROWSE PIECES
              </Link>
            </div>
          </div>
        </section>
      );
    }
    return (
      <section className="screen">
        <div className="scroll narrow-scroll">
          <div className="ck-head">
            <h1>CHECKOUT</h1>
            <div className="p">
              Sign in or create an account to complete your order — your bag stays
              just as you left it.
            </div>
          </div>
          {lines.map((l) => (
            <div key={l.id} className="ck-line">
              <div
                className="img"
                style={l.main_image_url ? { backgroundImage: `url(${l.main_image_url})` } : undefined}
              />
              <div>
                <div className="brand-name">{l.brand_name ?? '—'}</div>
                <div className="item-name">{l.name}</div>
                <div className="meta">
                  {l.credit_price} {l.credit_price === 1 ? 'CREDIT' : 'CREDITS'}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    removeFromGuestBasket(l.id);
                    void refresh();
                  }}
                  className="remove"
                  style={{ marginTop: 6 }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
          <div className="ck-totals">
            <div className="row total">
              <span>TOTAL</span>
              <span>
                {guestTotal} {guestTotal === 1 ? 'CREDIT' : 'CREDITS'}
              </span>
            </div>
            <div className="row delivery">
              <span>Delivery</span>
              <span className="v" style={{ color: 'var(--ink)' }}>£4.99</span>
            </div>
          </div>
          <div className="footer-cta pad-bottom" style={{ paddingTop: 18 }}>
            <button
              type="button"
              className="cta-full"
              onClick={() => navigate('/sign-in', { state: { from: '/checkout' } })}
            >
              <span>SIGN IN TO COMPLETE ORDER</span>
              <Arrow />
            </button>
            <div className="form-link" style={{ marginTop: 10, textAlign: 'center' }}>
              New to Label Rotation?{' '}
              <Link to="/sign-up" state={{ from: '/checkout' }}>Create an account</Link>
            </div>
          </div>
        </div>
      </section>
    );
  }
  if (!basket || basket.items.length === 0) {
    return (
      <section className="screen">
        <div className="scroll narrow-scroll">
          <div className="ck-head">
            <h1>CHECKOUT</h1>
            <div className="p">Your bag is empty.</div>
          </div>
          <div className="empty-grid">
            <div className="h">Nothing reserved yet</div>
            <div className="p">Browse the rotation and add your first piece.</div>
            <Link to="/browse" className="chip" style={{ background: 'var(--ink)', color: '#fff' }}>
              BROWSE PIECES
            </Link>
          </div>
        </div>
      </section>
    );
  }
  const totalCr = preview?.credits_required ?? 0;
  const balance = preview?.current_balance ?? 0;
  const deliveryFee = (preview?.delivery_fee_pence ?? 499) / 100;
  const balanceCovers = balance >= totalCr;
  const canCheckout = balanceCovers;
  return (
    <section className="screen">
      <div className="scroll narrow-scroll">
        <div className="ck-head">
          <h1>CHECKOUT</h1>
          <div className="p">Review your rotation</div>
        </div>
         {/* ID reminder hidden — remove surrounding comment to re-enable
        {profile && profile.id_status !== 'approved' && (
          <Link
            to="/verify-id"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              margin: '0 16px 14px',
              padding: '12px 14px',
              borderRadius: 10,
              background:
                profile.id_status === 'pending' ? 'var(--card)' : 'var(--warn-bg)',
              color:
                profile.id_status === 'pending' ? 'var(--ink-soft)' : 'var(--warn)',
              fontSize: 12,
              fontWeight: 600,
              textDecoration: 'none'
            }}
          >
            <span>
              {profile.id_status === 'pending'
                ? '⏳ ID under review — you can still check out, your order will ship once we verify.'
                : profile.id_status === 'rejected'
                  ? '⚠ ID was rejected — please re-upload before your order can ship.'
                  : '⚠ Verify your ID — required before your first order ships.'}
            </span>
            <span style={{ fontSize: 11, letterSpacing: '0.14em', whiteSpace: 'nowrap' }}>
              {profile.id_status === 'pending' ? 'STATUS →' : 'ADD ID →'}
            </span>
          </Link>
        )}
        */}
        {msLeft !== null && msLeft > 0 && msLeft <= 10 * 60 * 1000 && (
          <div
            style={{
              margin: '0 16px 14px',
              padding: '10px 14px',
              borderRadius: 10,
              background: msLeft < 60_000 ? 'var(--warn-bg)' : 'var(--card)',
              color: msLeft < 60_000 ? 'var(--warn)' : 'var(--ink-soft)',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 600
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            <span>
              We&rsquo;re holding these items for you for{' '}
              <strong>{formatCountdown(msLeft)}</strong>. Check out before then or
              they&rsquo;ll go back into rotation.
            </span>
          </div>
        )}
        {basket.items.map((bi) => (
          <div key={bi.id} className="ck-line">
            <div
              className="img"
              style={bi.main_image_url ? { backgroundImage: `url(${bi.main_image_url})` } : undefined}
            />
            <div>
              <div className="brand-name">{bi.brand_name ?? '—'}</div>
              <div className="item-name">{bi.name}</div>
              <div className="meta">
                {bi.credit_price} {bi.credit_price === 1 ? 'CREDIT' : 'CREDITS'}
              </div>
              <button onClick={() => void removeItem(bi.id)} className="remove" style={{ marginTop: 6 }}>
                Remove
              </button>
            </div>
          </div>
        ))}
        <div className="ck-totals">
          <div className="row total">
            <span>TOTAL</span>
            <span>
              {totalCr} {totalCr === 1 ? 'CREDIT' : 'CREDITS'}
            </span>
          </div>
          <div className="row delivery">
            <span>Delivery</span>
            <span className="v" style={{ color: 'var(--ink)' }}>£{deliveryFee.toFixed(2)}</span>
          </div>
          <div className="row">
            <span>Your balance</span>
            <span>
              {balance} {balance === 1 ? 'CREDIT' : 'CREDITS'}
            </span>
          </div>
          {!balanceCovers && (
            <>
              <div className="pill-bad">
                <Info />
                You don&rsquo;t have enough credits — top up to check out.
              </div>
              <Link to="/credits" className="add-btn">
                <Arrow small />
                Add credits
              </Link>
            </>
          )}
          {canCheckout && (
            <div className="pill-good">
              <Check small /> You have enough credits.
            </div>
          )}
        </div>
        {canCheckout && (
          <div style={{ padding: '14px 22px 6px' }}>
            <div
              style={{
                fontFamily: 'var(--display)',
                fontSize: 13,
                letterSpacing: '0.08em',
                margin: '0 0 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <PackageIcon /> DELIVERY ADDRESS
            </div>
            <div className="form-field">
              <label>Address line 1</label>
              <input
                type="text"
                value={shipping.shipping_line1}
                onChange={(e) => setShipping((s) => ({ ...s, shipping_line1: e.target.value }))}
                placeholder="123 High Street"
                autoComplete="address-line1"
              />
            </div>
            <div className="form-field">
              <label>Address line 2 (optional)</label>
              <input
                type="text"
                value={shipping.shipping_line2}
                onChange={(e) => setShipping((s) => ({ ...s, shipping_line2: e.target.value }))}
                placeholder="Flat 4B"
                autoComplete="address-line2"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-field">
                <label>City / town</label>
                <input
                  type="text"
                  value={shipping.shipping_city}
                  onChange={(e) => setShipping((s) => ({ ...s, shipping_city: e.target.value }))}
                  placeholder="London"
                  autoComplete="address-level2"
                />
              </div>
              <div className="form-field">
                <label>Postcode</label>
                <input
                  type="text"
                  value={shipping.shipping_postcode}
                  onChange={(e) =>
                    setShipping((s) => ({ ...s, shipping_postcode: e.target.value.toUpperCase() }))
                  }
                  placeholder="SW1A 1AA"
                  autoComplete="postal-code"
                />
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-soft)', marginTop: 4 }}>
              We&rsquo;ll save this to your profile so you don&rsquo;t need to retype it next time.
            </div>
          </div>
        )}
        <div className="ck-perks">
          <div className="row"><span className="ic"><CalendarIcon /></span>30 day rotation</div>
          <div className="row"><span className="ic"><PackageIcon /></span>Free rotation returns</div>
          <div className="row"><span className="ic"><Lock /></span>Credits never expire</div>
        </div>
        <div className="footer-cta pad-bottom">
          {canCheckout ? (
            <>
              <div className="consent">
                <button
                  className={`checkbox${consent ? ' checked' : ''}`}
                  type="button"
                  role="checkbox"
                  aria-checked={consent}
                  onClick={() => setConsent((v) => !v)}
                >
                  {consent && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
                <div className="text">
                  I agree to Label Rotation&apos;s{' '}
                  <Link to="/terms">Terms &amp; Conditions</Link>,{' '}
                  <Link to="/returns">Returns &amp; Rotation Policy</Link> and{' '}
                  <Link to="/privacy">Privacy Policy</Link>. I understand my card may be
                  charged for any applicable late fees or damage charges.
                </div>
                <div className="secure">
                  <div className="lock"><Lock /></div>
                  <div className="text">Secure checkout. Your card is stored via Stripe.</div>
                </div>
              </div>
              <button
                className={`cta-full${!consent || placing || !shippingValid ? ' disabled' : ''}`}
                disabled={!consent || placing || !shippingValid}
                title={!shippingValid ? 'Please fill in your delivery address' : undefined}
                onClick={() => void placeOrder()}
              >
                <span>{placing ? 'REDIRECTING TO STRIPE…' : 'CONFIRM ROTATION'}</span>
                <Arrow />
              </button>
              <div
                className="micro"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 10,
                  fontSize: 11,
                  color: 'var(--ink-soft)'
                }}
              >
                £4.99 delivery · Using your credits
              </div>
            </>
          ) : (
            <Link to="/credits" className="cta-full">
              <span>ADD CREDITS</span>
              <Arrow />
            </Link>
          )}
          {error && (<div className="form-error" style={{ marginTop: 10 }}>{error}</div>)}
        </div>
      </div>
    </section>
  );
}
function formatCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
function Arrow({ small = false }: { small?: boolean }) {
  const s = small ? 16 : 20;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
function Check({ small = false }: { small?: boolean }) {
  const s = small ? 16 : 40;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function Info() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16h0" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </svg>
  );
}
function PackageIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 16V8l-9-5-9 5v8l9 5 9-5z" />
      <path d="M3.3 7l8.7 5 8.7-5" />
    </svg>
  );
}
function TruckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="7" width="15" height="10" rx="1" />
      <path d="M16 11h4l3 3v3h-7" />
      <circle cx="6" cy="19" r="2" />
      <circle cx="19" cy="19" r="2" />
    </svg>
  );
}
function MailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}
function Lock() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 018 0v4" />
    </svg>
  );
}
